import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Clock3 } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import NewsletterCard from "@/components/newsletter/NewsletterCard";
import { newsletterApi } from "@/lib/newsletterApi";

const renderParagraphs = (content) =>
  String(content || "")
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);

const BlogDetailPage = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");

  const handleContactClick = () => {
    window.location.assign("/contact");
  };

  useEffect(() => {
    const loadBlog = async () => {
      try {
        const data = await newsletterApi.getBlog(slug);
        setBlog(data.blog);
        setRelatedBlogs(data.relatedBlogs || []);
      } catch (error) {
        setErrorMessage(error.response?.data?.message || "Unable to load this blog.");
      }
    };

    loadBlog();
  }, [slug]);

  if (errorMessage) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar onContactClick={() => window.location.assign("/")} />
        <div className="mx-auto flex min-h-[60vh] max-w-4xl items-center justify-center px-4 pt-32 text-center">
          <div>
            <p className="text-lg text-gray-600">{errorMessage}</p>
            <Link to="/blog" className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-purple-700">
              <ArrowLeft className="h-4 w-4" />
              Back to blog
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar onContactClick={() => window.location.assign("/")} />
        <div className="mx-auto max-w-4xl px-4 pt-32 text-center text-gray-600">Loading blog...</div>
      </div>
    );
  }

  const paragraphs = renderParagraphs(blog.content);

  return (
    <div className="min-h-screen bg-white">
      <Navbar onContactClick={() => window.location.assign("/")} />

      <section className="section-gradient-light pt-24">
        <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-bold text-purple-700">
            <ArrowLeft className="h-4 w-4" />
            Back to all blogs
          </Link>

          <div className="mt-5 max-w-4xl">
            <div className="mb-4 flex flex-wrap items-center gap-3 text-sm">
              <span className="rounded-full bg-purple-50 px-3 py-1 font-semibold text-purple-700">
                {blog.category}
              </span>
              <span className="text-gray-500">{new Date(blog.publishedAt).toLocaleDateString()}</span>
              <span className="inline-flex items-center gap-1 text-gray-500">
                <Clock3 className="h-4 w-4" />
                {blog.readingTime} min read
              </span>
            </div>
            <h1 className="font-heading text-3xl font-black leading-tight text-gray-900 sm:text-5xl">
              {blog.title}
            </h1>
            <p className="mt-4 text-base leading-7 text-gray-600 sm:text-lg">{blog.excerpt}</p>
          </div>
        </div>
      </section>

      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
        {blog.coverImage ? (
          <img
            src={blog.coverImage}
            alt={blog.title}
            className="h-[300px] w-full rounded-[24px] object-cover shadow-[0_18px_50px_rgba(123,31,162,0.12)] sm:h-[360px]"
          />
        ) : (
          <div className="flex h-[300px] w-full items-end rounded-[24px] bg-[linear-gradient(135deg,#f8dbff_0%,#efe4ff_45%,#d9d7ff_100%)] p-6 shadow-[0_18px_50px_rgba(123,31,162,0.12)] sm:h-[360px]">
            <div className="max-w-xl rounded-[22px] bg-white/70 p-6 backdrop-blur">
              <div className="text-xs font-bold uppercase tracking-[0.3em] text-purple-700">{blog.category}</div>
              <div className="mt-3 font-heading text-2xl font-black text-gray-900 sm:text-3xl">{blog.title}</div>
            </div>
          </div>
        )}

        <div className="mt-8 grid gap-6 lg:grid-cols-[minmax(0,1fr)_280px]">
          <article className="newsletter-prose rounded-[24px] border border-purple-100 bg-white p-5 shadow-[0_18px_50px_rgba(123,31,162,0.08)] sm:p-7">
            {paragraphs.map((paragraph, index) => (
              <motion.p key={`${blog._id}-${index}`} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                {paragraph}
              </motion.p>
            ))}
          </article>

          <aside className="space-y-4">
            <button
              type="button"
              onClick={handleContactClick}
              className="primary-btn w-full rounded-[22px] px-5 py-4 text-sm font-bold text-white"
            >
              Book Consultation
            </button>

            <div className="rounded-[22px] bg-gray-900 p-5 text-white">
              <div className="text-[11px] uppercase tracking-[0.3em] text-purple-200">Author</div>
              <h2 className="mt-2 font-heading text-xl font-bold">{blog.authorName}</h2>
              <p className="mt-3 text-sm leading-6 text-gray-300">
                Built for readers who want deeper thinking on growth systems, creative execution, and brand momentum.
              </p>
            </div>

            {!!blog.highlights?.length && (
              <div className="rounded-[22px] border border-purple-100 bg-purple-50/70 p-5">
                <div className="text-[11px] uppercase tracking-[0.3em] text-purple-700">Key takeaways</div>
                <div className="mt-4 space-y-2.5">
                  {blog.highlights.map((item) => (
                    <div key={item} className="rounded-xl bg-white px-3.5 py-3 text-sm font-medium leading-6 text-gray-700 shadow-sm">
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!!blog.tags?.length && (
              <div className="rounded-[22px] border border-purple-100 bg-white p-5">
                <div className="text-[11px] uppercase tracking-[0.3em] text-purple-700">Tags</div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {blog.tags.map((tag) => (
                    <span key={tag} className="rounded-full bg-purple-50 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-purple-700">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </aside>
        </div>

        {!!relatedBlogs.length && (
          <section className="mt-12">
            <div className="mb-5">
              <div className="text-[11px] uppercase tracking-[0.3em] text-purple-700">Related reads</div>
              <h2 className="mt-2 font-heading text-3xl font-black text-gray-900">More from the blog</h2>
            </div>
            <div className="grid gap-5 lg:grid-cols-3">
              {relatedBlogs.map((item) => (
                <NewsletterCard key={item._id} article={item} linkBase="/blog" />
              ))}
            </div>
          </section>
        )}
      </main>

      <Footer onContactClick={() => window.location.assign("/")} />
    </div>
  );
};

export default BlogDetailPage;
